<template>
    <ul class="custom-pagination" v-if="data.last_page > 1">
      <!-- Previous Page Link -->
      <li class="pagination-item" :class="{ 'disabled': !hasPrev }">
        <a class="pagination-link" href="#" @click.prevent="changePage(current_page-1)" aria-label="Previous">
          <span aria-hidden="true">&laquo;</span>
        </a>
      </li>
  
      <!-- First Page Link (always shown) -->
      <li class="pagination-item" :class="{ 'active': current_page === 1 }">
        <a class="pagination-link" href="#" @click.prevent="changePage(1)">1</a>
      </li>
  
      <!-- Left Ellipsis -->
      <li class="pagination-item ellipsis" v-if="showLeftEllipsis">
        <span class="pagination-link">...</span>
      </li>
  
      <!-- Middle Pages -->
      <li 
        v-for="page in middlePages" 
        :key="page" 
        class="pagination-item" 
        :class="{ 'active': page === current_page }"
      >
        <a class="pagination-link" href="#" @click.prevent="changePage(page)">{{ page }}</a>
      </li>
  
      <!-- Right Ellipsis -->
      <li class="pagination-item ellipsis" v-if="showRightEllipsis">
        <span class="pagination-link">...</span>
      </li>
  
      <!-- Last Page Link (always shown when last_page > 1) -->
      <li 
        v-if="data.last_page > 1" 
        class="pagination-item" 
        :class="{ 'active': current_page === data.last_page }"
      >
        <a class="pagination-link" href="#" @click.prevent="changePage(data.last_page)">{{ data.last_page }}</a>
      </li>
  
      <!-- Next Page Link -->
      <li class="pagination-item" :class="{ 'disabled': !hasNext }">
        <a class="pagination-link" href="#" @click.prevent="changePage(current_page+1)" aria-label="Next">
          <span aria-hidden="true">&raquo;</span>
        </a>
      </li>
    </ul>
  </template>
  
  <script>
  export default {
    props: {
      data: {
        type: Object,
        required: true
      },
      limit: {
        type: Number,
        default: 2
      }
    },
    computed: {
      current_page() {
        return this.data.current_page || 1;
      },
      hasPrev() {
        return this.current_page > 1;
      },
      hasNext() {
        return this.current_page < this.data.last_page;
      },
      showLeftEllipsis() {
        return this.current_page > (2 + this.limit);
      },
      showRightEllipsis() {
        return this.current_page < (this.data.last_page - this.limit - 1);
      },
      middlePages() {
        let pages = [];
        
        if (this.data.last_page <= 1) {
          return pages;
        }
        
        let from = Math.max(2, this.current_page - this.limit);
        let to = Math.min(this.data.last_page - 1, this.current_page + this.limit);
        
        // Adjust 'from' if we're showing right ellipsis
        if (this.showRightEllipsis) {
          to = Math.min(this.current_page + this.limit, this.data.last_page - 2);
        }
        
        // Adjust 'to' if we're showing left ellipsis
        if (this.showLeftEllipsis) {
          from = Math.max(this.current_page - this.limit, 3);
        }
        
        // Always show at least (limit * 2 + 1) pages if available
        if (to - from + 1 < (this.limit * 2 + 1) && this.data.last_page >= (this.limit * 2 + 3)) {
          if (from === 3) {
            to = Math.min(from + (this.limit * 2), this.data.last_page - 2);
          } else if (to === this.data.last_page - 2) {
            from = Math.max(to - (this.limit * 2), 3);
          }
        }
        
        for (let i = from; i <= to; i++) {
          pages.push(i);
        }
        
        return pages;
      }
    },
    methods: {
      changePage(page) {
        if (page === this.current_page || page < 1 || page > this.data.last_page) {
          return;
        }
        
        this.$emit('pagination-change-page', page);
      }
    }
  }
  </script>
  
  <style scoped>
  .custom-pagination {
    display: flex;
    list-style: none;
    padding: 0;
    margin: 0;
    gap: 0.25rem;
  }
  
  .pagination-item {
    margin: 0 2px;
  }
  
  .pagination-link {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 38px;
    height: 38px;
    padding: 0.25rem 0.75rem;
    border-radius: 6px;
    border: 1px solid #dee2e6;
    background-color: white;
    color: #0A0446;
    font-weight: 500;
    text-decoration: none;
    transition: all 0.2s ease;
    cursor: pointer;
  }
  
  .pagination-link:hover {
    background-color: #f8f9fa;
    border-color: #dee2e6;
    color: #0A0446;
  }
  
  .pagination-item.active .pagination-link {
    background-color: #0A0446;
    border-color: #0A0446;
    color: white;
  }
  
  .pagination-item.disabled .pagination-link {
    color: #6c757d;
    pointer-events: none;
    background-color: #fff;
    border-color: #dee2e6;
    opacity: 0.65;
  }
  
  .pagination-item.ellipsis .pagination-link {
    border: none;
    pointer-events: none;
  }
  </style>


// WEBPACK FOOTER //
// src/components/common/CustomPagination.vue